import { Phone, Mail, MapPin, Clock, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { BRAND } from "../data/brand";
import WhatsAppIcon from "../components/WhatsAppIcon";

export default function Contatti() {
  return (
    <div data-testid="contatti-page">
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="container-90 py-20 lg:py-24">
          <div className="max-w-4xl">
            <div className="overline mb-6">Contatti</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
              Parliamo della tua
              <br />
              <span className="text-[color:var(--brand-blue)]">prossima polizza.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Chiamaci, scrivici o passa a trovarci nel nostro ufficio di Frosinone.
              La prima consulenza è sempre gratuita e senza impegno.
            </p>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="section bg-white">
        <div className="container-90">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="contact-phone-card"
              className="group block p-8 bg-slate-50 border border-slate-200 hover:border-[color:var(--brand-blue)] hover:bg-white transition-all"
            >
              <Phone className="w-7 h-7 text-[color:var(--brand-blue)] mb-6" strokeWidth={1.5} />
              <div className="overline mb-3">Telefono</div>
              <div className="font-heading text-xl font-bold text-slate-900 group-hover:text-[color:var(--brand-blue)] transition-colors">
                {BRAND.phone}
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-600">
                Chiamaci <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </a>

            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="group block p-8 bg-[#25D366] border border-[#25D366] hover:bg-[#1ebe57] transition-all text-white"
            >
              <WhatsAppIcon className="w-7 h-7 mb-6" />
              <div className="overline mb-3 text-white/80">WhatsApp</div>
              <div className="font-heading text-xl font-bold">
                {BRAND.phone}
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white/90">
                Scrivici ora <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </a>

            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email-card"
              className="group block p-8 bg-slate-50 border border-slate-200 hover:border-[color:var(--brand-blue)] hover:bg-white transition-all"
            >
              <Mail className="w-7 h-7 text-[color:var(--brand-blue)] mb-6" strokeWidth={1.5} />
              <div className="overline mb-3">Email</div>
              <div className="font-heading text-base font-bold text-slate-900 group-hover:text-[color:var(--brand-blue)] transition-colors break-all">
                {BRAND.email}
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-600">
                Scrivici <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </a>

            <div
              data-testid="contact-address-card"
              className="block p-8 bg-slate-50 border border-slate-200"
            >
              <MapPin className="w-7 h-7 text-[color:var(--brand-blue)] mb-6" strokeWidth={1.5} />
              <div className="overline mb-3">Sede</div>
              <div className="font-heading text-base font-bold text-slate-900 leading-tight">
                {BRAND.address.street}
                <br />
                03100 {BRAND.address.city} (FR)
              </div>
              <div className="mt-5 inline-flex items-start gap-2 text-sm text-slate-600 leading-snug">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {BRAND.hours.weekdays}
                  <br />
                  {BRAND.hours.saturday}
                </span>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-slate-200">
            <div className="lg:col-span-4 p-10 lg:p-12 bg-[#0a0f1f] text-white">
              <div className="overline text-white/60 mb-4">La nostra sede</div>
              <h2 className="font-heading text-3xl font-extrabold leading-tight mb-6">
                Ti aspettiamo a Frosinone.
              </h2>
              <p className="text-slate-300 leading-relaxed mb-8">
                Il nostro ufficio si trova in Via Piave 67, nel cuore della città. Passa a
                trovarci per una consulenza di persona.
              </p>

              <ul className="space-y-5 mb-10">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[color:var(--brand-blue-light)] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">{BRAND.address.full}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[color:var(--brand-blue-light)] mt-0.5 flex-shrink-0" />
                  <a
                    href={`tel:${BRAND.phoneRaw}`}
                    className="text-slate-200 hover:text-white font-heading"
                  >
                    {BRAND.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[color:var(--brand-blue-light)] mt-0.5 flex-shrink-0" />
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="text-slate-200 hover:text-white break-all"
                  >
                    {BRAND.email}
                  </a>
                </li>
              </ul>

              <div className="pt-8 border-t border-white/10">
                <div className="overline text-white/60 mb-4">Seguici</div>
                <div className="flex gap-3">
                  {BRAND.social.map((s, i) => (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${i}-link`}
                      className="w-11 h-11 border border-white/20 flex items-center justify-center hover:bg-[color:var(--brand-blue)] hover:border-[color:var(--brand-blue)] transition-colors"
                      aria-label={s.handle}
                    >
                      {s.network === "Instagram" ? (
                        <Instagram className="w-5 h-5" />
                      ) : (
                        <Facebook className="w-5 h-5" />
                      )}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 min-h-[400px] lg:min-h-0">
              <iframe
                title="90 Brokers Srls - Mappa"
                data-testid="google-map-iframe"
                src="https://www.google.com/maps?q=Via+Piave+67,+Frosinone&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "500px", display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Direct contact CTA */}
          <div className="mt-20 text-center max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight mb-4">
              Preferisci che ti richiamiamo noi?
            </h3>
            <p className="text-slate-600 leading-relaxed mb-8">
              Chiama direttamente il numero dedicato: un consulente è pronto ad ascoltarti e a
              offrirti un preventivo gratuito su misura.
            </p>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="contatti-call-btn"
              className="btn-primary"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
